import React, { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useSnackbar } from 'notistack';

// Data
import { buildCreatePlayerAssessments } from '../../../lib/services/player-assessment.service';
import { getAuthUser } from '../../../lib/services/auth.service';
import { canEditAttendanceDefault } from '../../Attendance/attendance.permissions';
import { buildCreateAgeGroup } from '../../../lib/services/age-group.service';

// Components
import { ImportPlayersContainer } from './import-players.container';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { MessageLines } from '../../_core/_ui/typography.component';
import { useSnackbarDismissAction } from '../../../lib/hooks/use-snackbar-dismiss-action';
import { useOperationErrorHandler } from '../../../lib/hooks/use-operation-error-handler';
import { getImportPlayersMessages } from './import-players.messages';
import { groupImportFailures, IImportFailureGroups, IImportOutcomeSummary, summarizeImportOutcomes } from '../../../lib/helpers/player.helpers';
import { pluralize, summarizeList } from '../../../lib/helpers/text.helpers';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { useCompanySport, useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { IImportSubmissionSummary } from '../../_core/Importer/importer.interface';
import { FAILURE_SNACKBAR_MS, OUTCOME_SNACKBAR_MS } from '../../../lib/snackbar.constants';

// A failed import is usually a handful of rows; past this the operator wants the
// count and a sample, not a wall of names.
const NAMED_ROW_CAP = 5;

const GET_IMPORT_PLAYERS = gql`
  query GetImportPlayers($assessmentId: ID!, $companyId: ID!, $includeGlobalForSport: SPORT, $withJerseySettings: Boolean!) {
    positions(companyId: $companyId, includeGlobalForSport: $includeGlobalForSport) {
      _id
      name
    }
    ageGroups(assessmentId: $assessmentId) {
      _id
      name
    }
    assessment(_id: $assessmentId) @include(if: $withJerseySettings) {
      _id
      retainJerseyAssignments
    }
    company(_id: $companyId) @include(if: $withJerseySettings) {
      _id
      retainJerseyAssignments
    }
  }
`;

interface IImportPlayersGraphQL {
  onComplete?: Function;
  setSubmitError?: Function;
  setImportingRowCount?: Function;
}

export const ImportPlayersGraphQL: React.FC<IImportPlayersGraphQL> = ({ onComplete, setSubmitError, setImportingRowCount }) => {
  const user = getAuthUser();
  const sport = useCompanySport()
  const termLookup = useCompanySportTerminology();
  const messages = useMemo(() => getImportPlayersMessages(termLookup), [termLookup]);
  const athleteTerm = termLookup[SportTerminology.ATHLETE].toLowerCase();
  const { enqueueSnackbar } = useSnackbar();
  const dismissAction = useSnackbarDismissAction();
  const handleImportError = useOperationErrorHandler(messages.errImportFailed);

  // The same gate as the assessment settings form, which is what the import's jersey question changes.
  const canSetJerseyRetention = canEditAttendanceDefault();

  const { data } = useQuery(GET_IMPORT_PLAYERS, {
    skip: !user,
    variables: {
      assessmentId: user?.settings.activeAssessmentId,
      companyId: user?.settings.activeCompanyId,
      includeGlobalForSport: sport,
      withJerseySettings: canSetJerseyRetention
    },
    fetchPolicy: 'cache-and-network'
  });
  // `data` is undefined until the query resolves and again whenever cache-and-network clears it,
  // so a bare `|| []` would hand a fresh array down and churn the container's column memos.
  const ageGroups = useMemo(() => data?.ageGroups || [], [data?.ageGroups]);
  const positions = useMemo(() => data?.positions || [], [data?.positions]);
  // Display only: the server resolves the setting the same way, assessment first.
  const currentlyRetainsJerseys = Boolean(data?.assessment?.retainJerseyAssignments ?? data?.company?.retainJerseyAssignments);

  const { createPlayerAssessments } = buildCreatePlayerAssessments();
  const { createAgeGroup } = buildCreateAgeGroup();

  // Values for the count-based copy: row-shaped lines count file rows, outcome
  // lines count athletes, and the contact lines count contact records.
  const rowValues = (count: number, names?: string) => ({ count, noun: pluralize(count, 'row'), names });
  const athleteValues = (count: number) => ({ count, noun: pluralize(count, athleteTerm, pluralTerm(athleteTerm)) });
  const contactValues = (count: number) => ({ count, noun: pluralize(count, 'contact') });

  // One line per thing that actually happened — every count the server reports,
  // never a bare "imported" that hides a skip or a merge behind it.
  const describeOutcomes = (summary: IImportOutcomeSummary) => {
    const lines: React.ReactNode[] = [];

    if (summary.created) lines.push(<div key="created"><IntlMsg {...messages.snackCreated} values={athleteValues(summary.created)} /></div>);
    if (summary.merged) lines.push(<div key="merged"><IntlMsg {...messages.snackMerged} values={athleteValues(summary.merged)} /></div>);
    if (summary.skipped) lines.push(<div key="skipped"><IntlMsg {...messages.snackSkipped} values={athleteValues(summary.skipped)} /></div>);
    if (summary.duplicateRows) lines.push(<div key="duplicateRows"><IntlMsg {...messages.snackDuplicateRows} values={rowValues(summary.duplicateRows)} /></div>);
    if (summary.contactsCreated) lines.push(<div key="contactsCreated"><IntlMsg {...messages.snackContactsCreated} values={contactValues(summary.contactsCreated)} /></div>);
    if (summary.contactsMerged) lines.push(<div key="contactsMerged"><IntlMsg {...messages.snackContactsMerged} values={contactValues(summary.contactsMerged)} /></div>);

    return lines;
  };

  // One line per reason the rows failed — the fix differs per group, so they are
  // never collapsed into a single "some players failed".
  const describeFailures = (groups: IImportFailureGroups) => {
    const lines: React.ReactNode[] = [];

    if (groups.nameTakenDated.length) lines.push(<div key="nameTakenDated"><IntlMsg {...messages.errNameTaken}
      values={rowValues(groups.nameTakenDated.length,
        summarizeList(groups.nameTakenDated.map(entry => `${entry.name} (on file: ${entry.dateOfBirth})`), NAMED_ROW_CAP))} /></div>);

    if (groups.nameTakenUndated.length) lines.push(<div key="nameTakenUndated"><IntlMsg {...messages.errNameTakenUnknownDob}
      values={rowValues(groups.nameTakenUndated.length,
        summarizeList(groups.nameTakenUndated.map(entry => entry.name), NAMED_ROW_CAP))} /></div>);

    if (groups.invalid.length) lines.push(<div key="invalid"><IntlMsg {...messages.errInvalid}
      values={rowValues(groups.invalid.length,
        summarizeList(groups.invalid.map(entry => `${entry.name} (${entry.details.join('; ')})`), NAMED_ROW_CAP))} /></div>);

    if (groups.unknown.length) lines.push(<div key="unknown"><IntlMsg {...messages.errUnknown}
      values={rowValues(groups.unknown.length,
        summarizeList(groups.unknown.map(entry => entry.name), NAMED_ROW_CAP))} /></div>);

    return lines;
  };

  const handleCreate = (
    rows: IPlayerAssessment[],
    summary: IImportSubmissionSummary,
    fillMissingDateOfBirth?: DateOfBirthFallback,
    overwriteDateOfBirth?: boolean,
    retainJerseyAssignments?: boolean
  ) => {
    if (setSubmitError) setSubmitError(null)
    const data = rows.map(row => ({ ...row, assessmentId: user?.settings.activeAssessmentId }));
    // Rows the review step withheld never reached the server, so they are only ever counted here.
    const skippedCount = summary.skippedRowCount;

    // Set before the panel closes, so the list says an import is running from the moment it does.
    if (setImportingRowCount) setImportingRowCount(data.length);
    if (onComplete) onComplete();

    return createPlayerAssessments(data, fillMissingDateOfBirth, overwriteDateOfBirth, retainJerseyAssignments).then((results) => {
      const rowResults: ICreatePlayerAssessmentRowResult[] = results.data?.createPlayerAssessments || [];
      const summary = summarizeImportOutcomes(rowResults, skippedCount);

      // errorPolicy 'all' delivers a partial failure here rather than rejecting. With nothing
      // written there is no outcome to report, so it stays the plain failure it used to be.
      if (results.errors?.length && !summary.created && !summary.merged) {
        handleImportError('createPlayerAssessments', results.errors);

        return;
      }

      const outcomeLines = describeOutcomes(summary);
      const failureLines = describeFailures(groupImportFailures(data, rowResults));
      // Rows the server never resolved: no per-row failure names them, so they are reported once.
      const erroredLines = results.errors?.length
        ? [<div key="errored"><IntlMsg {...messages.errPartlyFailed} /></div>]
        : [];
      const lines = skippedCount
        ? [<div key="skipped"><IntlMsg {...messages.errSkipped} values={rowValues(skippedCount)} /></div>, ...failureLines, ...erroredLines]
        : [...failureLines, ...erroredLines];

      if (setSubmitError && lines.length) setSubmitError(<MessageLines>{lines}</MessageLines>)

      // Anything added makes this the success report; otherwise it only states
      // what was already there, which is information rather than success.
      if (outcomeLines.length) enqueueSnackbar(<MessageLines>{outcomeLines}</MessageLines>, {
        variant: summary.created || summary.merged ? 'success' : 'info',
        autoHideDuration: OUTCOME_SNACKBAR_MS,
        action: dismissAction
      });

      // Two different fixes, so they are never one count: a withheld row never left the browser,
      // a refused one was rejected by the server.
      const notAddedLines: React.ReactNode[] = [];

      if (summary.withheld) notAddedLines.push(<div key="withheld"><IntlMsg {...messages.snackLeftOut} values={rowValues(summary.withheld)} /></div>);
      if (summary.refused) notAddedLines.push(<div key="refused"><IntlMsg {...messages.snackNotAdded} values={athleteValues(summary.refused)} /></div>);

      if (notAddedLines.length) enqueueSnackbar(<MessageLines>{notAddedLines}</MessageLines>,
        { variant: 'error', autoHideDuration: FAILURE_SNACKBAR_MS, action: dismissAction });
    }).catch((error: unknown) => handleImportError('createPlayerAssessments', error)).finally(() => {
      if (setImportingRowCount) setImportingRowCount(null);
    })
  }

  return <ImportPlayersContainer
    user={user}
    ageGroups={ageGroups}
    positions={positions}
    createPlayerAssessments={handleCreate}
    createAgeGroup={createAgeGroup}
    onImportError={handleImportError}
    canSetJerseyRetention={canSetJerseyRetention}
    currentlyRetainsJerseys={currentlyRetainsJerseys}
  />;
};
